import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, money } from "@/components/shared";
import { format, parseISO } from "date-fns";
import SmsWeekCard from "@/components/sms/SmsWeekCard";
import PhotoStrip from "@/components/photos/PhotoStrip";
import DashboardMap from "@/components/map/DashboardMap";
import RevenueChart from "@/components/dashboard/RevenueChart";
import MarginTable from "@/components/dashboard/MarginTable";

function Stat({ label, value }) {
  return (
    <div className="py-8 px-2" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="label-caps text-muted-foreground">{label}</div>
      <div className="font-serif text-5xl mt-3 tracking-tight text-brand-gradient">{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/jobs").then((r) => setJobs(r.data)).catch(() => {});
  }, []);

  const today = new Date().toDateString();
  const completedToday = jobs.filter((j) => j.completed_at && new Date(j.completed_at).toDateString() === today);

  const upcoming = jobs
    .filter((j) => j.status === "scheduled")
    .slice(0, 6);

  return (
    <div>
      <PageHeader
        eyebrow={`Welcome back, ${user?.name?.split(" ")[0] || ""}`}
        title="Dashboard"
        description="A calm overview of your bookings, team and revenue."
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-border/60 border-y border-border/60">
        <Stat label="Upcoming" value={stats?.upcoming_jobs ?? "—"} />
        <Stat label="Completed" value={stats?.completed_jobs ?? "—"} />
        <Stat label="Total Jobs" value={stats?.total_jobs ?? "—"} />
        <Stat label="Revenue" value={stats ? money(stats.revenue) : "—"} />
      </div>

      {user?.role === "admin" && (
        <div className="grid grid-cols-3 divide-x divide-border/60 border-b border-border/60">
          <Stat label="Clients" value={stats?.customers ?? "—"} />
          <Stat label="Team Members" value={stats?.team_members ?? "—"} />
          <Stat label="Packages" value={stats?.packages ?? "—"} />
        </div>
      )}

      {user?.role === "admin" && <RevenueChart />}
      {user?.role === "admin" && <MarginTable />}
      {user?.role === "admin" && <DashboardMap />}

      {user?.role === "admin" && <SmsWeekCard />}

      {user?.role === "admin" && completedToday.length > 0 && (
        <div className="mt-12" data-testid="completed-today">
          <div className="label-caps text-muted-foreground mb-4">Completed today</div>
          <div className="divide-y divide-border/60 border-y border-border/60">
            {completedToday.map((j) => (
              <div key={j.id} className="flex items-center justify-between py-4 text-sm" data-testid={`completed-today-${j.id}`}>
                <div>
                  <div><span className="font-medium">{j.customer?.name || "Client"}</span> <span className="text-muted-foreground">· {j.package?.name || "Clean"}</span></div>
                  <div className="text-muted-foreground text-xs mt-1">by {j.completed_by || "team"} at {format(parseISO(j.completed_at), "h:mma")}</div>
                </div>
                <PhotoStrip job={j} />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-12">
        <div className="label-caps text-muted-foreground mb-6">Next scheduled cleans</div>
        {upcoming.length === 0 ? (
          <p className="text-muted-foreground text-sm py-10 border border-dashed border-border/60 text-center">
            No upcoming bookings scheduled.
          </p>
        ) : (
          <div className="divide-y divide-border/60 border-y border-border/60">
            {upcoming.map((j) => (
              <div
                key={j.id}
                className="flex items-center justify-between gap-3 py-5"
                data-testid={`dash-job-${j.id}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-6 min-w-0">
                  <div className="font-serif text-xl sm:w-40 shrink-0">
                    {j.scheduled_date ? format(parseISO(j.scheduled_date), "EEE d MMM") : "—"}
                    <span className="text-muted-foreground text-sm ml-2">
                      {j.scheduled_date ? format(parseISO(j.scheduled_date), "h:mma") : ""}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium truncate">{j.customer?.name || "Unknown client"}</div>
                    <div className="text-sm text-muted-foreground">
                      {j.package?.name || "Custom clean"}
                      {j.team?.length ? ` · ${j.team.map((t) => t.name).join(", ")}` : ""}
                    </div>
                  </div>
                </div>
                <div className="shrink-0"><StatusBadge status={j.status} /></div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
